import React, { ReactElement, useEffect, useRef, useState } from 'react'
import {
  Button,
  Checkbox,
  Col,
  Dropdown,
  Form,
  Input,
  Menu,
  Modal,
  Popover,
  Radio,
  Row,
  Space,
  Switch,
  notification,
} from 'antd'
import { CalendarOutlined, DownOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import { RootDispatch } from '../../app/store'
import { Checks } from '../../models/setting'
import { selectSettings, getSettingDetail } from '../user/setting/settingSlice'
import { NavBar } from '../../compnents/nav/NavBar'
import SimpleAgGrid from '../../compnents/simpleTable/SimpleAgGrid'
import styles from './MembersManagementPage.module.css'
import { MemberPointsSpecifiedColumns, MemberPointsExchangeColumns } from './columns'
import { TreatmentBillingCategories } from '../../models/billingCategory'
import { deleteMemberPoint, getMemberPointConfigurationDetail, getMemberPointExceptionList, getMemberPointPage, submitMemberPoint, submitMemberPointConfiguration } from './MembersManagementPageSlice'
import { MemberPointConfigurationDetail, MemberPointConfigurationSubmitParams } from '../../services/members'
import { AddItemForPoints } from './memberPointsModals/AddItemForPoints'
import { AddTreatmentForPoints } from './memberPointsModals/AddTreatmentForPoints'
import { AddItemCategoryForPoints } from './memberPointsModals/AddItemCategoryForPoints'
import { AddTreatmentCategoryForPoints } from './memberPointsModals/AddTreatmentCategoryForPoints'

const MonthDayPicker = ({ value, onChange }: { value?: [number, number], onChange?: (val: [number, number]) => void }) => {
  const [open, setOpen] = useState(false)

  const [tempMonth, setTempMonth] = useState<number | undefined>(value?.[0])
  const [tempDay, setTempDay] = useState<number | undefined>(value?.[1])

  useEffect(() => {
    if (open) {
      setTempMonth(value?.[0])
      setTempDay(value?.[1])
    }
  }, [open, value])

  const getDaysInMonth = (m: number) => {
    if ([1, 3, 5, 7, 8, 10, 12].includes(m)) return 31
    if ([4, 6, 9, 11].includes(m)) return 30
    return 28
  }

  const handleMonthChange = (m: number) => {
    setTempMonth(m)
    if (tempDay) {
      const maxDays = getDaysInMonth(m)
      if (tempDay > maxDays) {
        setTempDay(maxDays)
      }
    }
  }

  const handleConfirm = () => {
    if (tempMonth && tempDay) {
      onChange?.([tempMonth, tempDay])
      setOpen(false)
    }
  }

  const displayValue = value?.[0] && value?.[1] ? `${String(value[0]).padStart(2, '0')}-${String(value[1]).padStart(2, '0')}` : ''
  const daysCount = tempMonth ? getDaysInMonth(tempMonth) : 31
  const canConfirm = !!tempMonth && !!tempDay

  const content = (
    <div className={styles.mdpContent}>
      <div className={styles.mdpBody}>
        <div className={styles.mdpMonthCol}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
            const isSelected = tempMonth === m
            return (
              <div
                key={m}
                onClick={() => handleMonthChange(m)}
                className={`${styles.mdpCell} ${isSelected ? styles.mdpCellSelected : ''}`}
              >
                {m}月
              </div>
            )
          })}
        </div>
        <div className={styles.mdpDayCol}>
          {Array.from({ length: daysCount }, (_, i) => i + 1).map((d) => {
            const isSelected = tempDay === d
            return (
              <div
                key={d}
                onClick={() => setTempDay(d)}
                className={`${styles.mdpCell} ${isSelected ? styles.mdpCellSelected : ''}`}
              >
                {d}
              </div>
            )
          })}
        </div>
      </div>
      <div className={styles.mdpFooter}>
        <Button size="small" type={canConfirm ? 'primary' : undefined} disabled={!canConfirm} onClick={handleConfirm} className={styles.mdpConfirmBtn}>
          确定
        </Button>
      </div>
    </div>
  )

  return (
    <Popover
      visible={open}
      onVisibleChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={content}
    >
      <Input
        readOnly
        value={displayValue}
        placeholder="清零日期"
        style={{ width: 140, cursor: 'pointer' }}
        suffix={<CalendarOutlined style={{ color: '#bfbfbf', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setOpen(!open) }} />}
        onClick={() => setOpen(!open)}
      />
    </Popover>
  )
}

interface SpecifiedProject {
  key: string
  id?: any
  name: string
  type: 'goods' | 'treatment' | 'goodsCategory' | 'treatmentCategory'
  exception?: string
  billingCategory?: string
}

interface ExchangeProject {
  key: string
  id?: any
  name: string
  point?: string | number
  type: 'goods' | 'treatment'
}

export const MemberPoints = ({ inTab = false }: { inTab?: boolean }): ReactElement => {
  const [form] = Form.useForm()
  const dispatch = useDispatch<RootDispatch>()
  const settings = useSelector(selectSettings)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [pointsEnabled, setPointsEnabled] = useState(false)
  const [scope, setScope] = useState(0)
  const [fixedClearDate, setFixedClearDate] = useState<[number, number] | undefined>()

  const [addItemVisible, setAddItemVisible] = useState(false)
  const [addTreatmentVisible, setAddTreatmentVisible] = useState(false)
  const [addItemCategoryVisible, setAddItemCategoryVisible] = useState(false)
  const [addTreatmentCategoryVisible, setAddTreatmentCategoryVisible] = useState(false)
  const [addTarget, setAddTarget] = useState<'scope' | 'exchange'>('scope')

  const [exceptionVisible, setExceptionVisible] = useState(false)
  const [exceptionRecord, setExceptionRecord] = useState<any>(null)
  const [exceptionCatalogType, setExceptionCatalogType] = useState<0 | 1>(0)
  const [exceptionDefaultKeys, setExceptionDefaultKeys] = useState<React.Key[]>([])
  const [exceptionDefaultRows, setExceptionDefaultRows] = useState<any[]>([])

  const [specifiedProjects, setSpecifiedProjects] = useState<SpecifiedProject[]>([])
  const [specifiedCurrent, setSpecifiedCurrent] = useState(1)
  const [specifiedSize, setSpecifiedSize] = useState(10)
  const [specifiedTotal, setSpecifiedTotal] = useState(0)
  const [specifiedLoading, setSpecifiedLoading] = useState(false)

  const [exchangeProjects, setExchangeProjects] = useState<ExchangeProject[]>([])
  const [exchangeCurrent, setExchangeCurrent] = useState(1)
  const [exchangeSize, setExchangeSize] = useState(10)
  const [exchangeTotal, setExchangeTotal] = useState(0)
  const [exchangeLoading, setExchangeLoading] = useState(false)

  const [editExchangeVisible, setEditExchangeVisible] = useState(false)
  const [editExchangeRecord, setEditExchangeRecord] = useState<any>(null)
  const [editExchangeForm] = Form.useForm()
  const [editExchangeLoading, setEditExchangeLoading] = useState(false)

  // 用于动态调整表格高度的 ref
  const specifiedGridRef = useRef<HTMLDivElement>(null)
  const exchangeGridRef = useRef<HTMLDivElement>(null)

  // 根据数据行数动态调整 AG Grid 高度（空数据时保留最小行数，避免高度塌陷）
  const adjustGridHeight = (containerRef: React.RefObject<HTMLDivElement>, rowCount: number) => {
    if (!containerRef.current) return
    const ROW_HEIGHT = 41
    const HEADER_HEIGHT = 41
    const PAGINATION_HEIGHT = 56
    const MIN_ROWS = 6
    const gridHeight = HEADER_HEIGHT + Math.max(rowCount, MIN_ROWS) * ROW_HEIGHT + PAGINATION_HEIGHT
    
    const container = containerRef.current
    const agRootWrapper = container.querySelector('.ag-root-wrapper') as HTMLElement
    const agTheme = container.querySelector('.ag-theme-alpine') as HTMLElement
    const agContainer = container.firstElementChild as HTMLElement
    
    if (agRootWrapper) agRootWrapper.style.height = `${gridHeight}px`
    if (agTheme) agTheme.style.height = `${gridHeight}px`
    if (agContainer) agContainer.style.height = `${gridHeight}px`
    container.style.height = `${gridHeight}px`
  }

  useEffect(() => {
    setPointsEnabled(!!settings?.[Checks.JFHD])
  }, [settings])

  useEffect(() => {
    if (!pointsEnabled) return
    dispatch(getMemberPointConfigurationDetail())
      .then(unwrapResult)
      .then((data: MemberPointConfigurationDetail) => {
        const resetType = data.resetType ?? 0
        const values: any = {
          obtainState: data.obtainState ?? 0,
          price: data.price ?? 1,
          point: data.point ?? 1,
          obtainRange: data.obtainRange ?? 0,
          resetType,
        }
        if (resetType === 2) {
          values.month = data.month
        }
        form.setFieldsValue(values)
        setScope(data.obtainRange ?? 0)
        if (resetType === 1 && data.month && data.day) {
          setFixedClearDate([data.month, data.day])
        }
      })
  }, [pointsEnabled])

  const fetchSpecifiedProjects = () => {
    setSpecifiedLoading(true)
    dispatch(getMemberPointPage({ isExchange: 1, current: specifiedCurrent, size: specifiedSize }))
      .then(unwrapResult)
      .then((res: any) => {
        const records = res?.records || []
        setSpecifiedProjects(records)
        setSpecifiedTotal(res?.total || 0)
        // 数据加载完成后调整高度
        setTimeout(() => adjustGridHeight(specifiedGridRef, records.length), 0)
      })
      .finally(() => setSpecifiedLoading(false))
  }

  const fetchExchangeProjects = () => {
    setExchangeLoading(true)
    dispatch(getMemberPointPage({ isExchange: 0, current: exchangeCurrent, size: exchangeSize }))
      .then(unwrapResult)
      .then((res: any) => {
        const records = res?.records || []
        setExchangeProjects(records)
        setExchangeTotal(res?.total || 0)
        // 数据加载完成后调整高度
        setTimeout(() => adjustGridHeight(exchangeGridRef, records.length), 0)
      })
      .finally(() => setExchangeLoading(false))
  }

  useEffect(() => {
    if (!pointsEnabled) return
    fetchSpecifiedProjects()
  }, [pointsEnabled, specifiedCurrent, specifiedSize])

  useEffect(() => {
    if (!pointsEnabled) return
    fetchExchangeProjects()
  }, [pointsEnabled, exchangeCurrent, exchangeSize])

  // 当数据或 scope 变化时，调整表格高度
  useEffect(() => {
    if (!pointsEnabled) return
    // 使用 setTimeout 确保 DOM 已更新
    setTimeout(() => {
      if (scope === 1) {
        adjustGridHeight(specifiedGridRef, specifiedProjects.length)
      }
      adjustGridHeight(exchangeGridRef, exchangeProjects.length)
    }, 0)
  }, [pointsEnabled, scope, specifiedProjects, exchangeProjects])

  const handleSpecifiedAction = (text: string, record: SpecifiedProject) => {
    if (text === 'delete') {
      Modal.confirm({
        title: '确认删除',
        content: `确定要删除「${record.name || ''}」吗？`,
        maskClosable: false,
        okText: '确定',
        cancelText: '取消',
        onOk: () => {
          dispatch(deleteMemberPoint({ id: record.id ?? record.key, type: 1 }))
            .then(unwrapResult)
            .then(() => {
              notification.success({ message: '删除成功' })
              fetchSpecifiedProjects()
            })
        },
      })
      return
    }
    if (text === 'editException') {
      const billingCategory = record.billingCategory
      if (!billingCategory) return
      const catalogType: 0 | 1 = (TreatmentBillingCategories as string[]).includes(billingCategory) ? 1 : 0
      setExceptionRecord(record)
      setExceptionCatalogType(catalogType)
      dispatch(getMemberPointExceptionList({ billingCategory, catalogType }))
        .then(unwrapResult)
        .then((res: any) => {
          const records: any[] = (res?.records || res || []).filter((v: any) => v && v.tenantCatalogId)
          // 用 tenantCatalogId 与 pageGoods 行的 id 对应，保证勾选回显；完整字段用于「查看已选」
          setExceptionDefaultKeys(records.map((v: any) => v.tenantCatalogId))
          setExceptionDefaultRows(records.map((v: any) => ({
            id: v.tenantCatalogId,
            tenantCatalogId: v.tenantCatalogId,
            name: v.name,
            retailPrice: v.retailPrice,
            stockPrice: v.stockPrice,
            costPrice: v.costPrice,
            spec: v.spec,
            specialNum: v.specialNum,
            tenantSupplierName: v.tenantSupplierName,
            materialsCategory: v.materialsCategory,
            packSpec: v.packSpec,
            model: v.model,
            doseCategory: v.doseCategory,
            totalCount: v.totalCount,
            accountUnit: v.accountUnit,
            totalSplitCount: v.totalSplitCount,
            splitUnit: v.splitUnit,
            billingCategory: v.billingCategory,
          })))
          setExceptionVisible(true)
        })
      return
    }
  }

  const handleExchangeAction = (text: string, record: ExchangeProject) => {
    if (text === 'delete') {
      Modal.confirm({
        title: '确认删除',
        content: `确定要删除「${record.name || ''}」吗？`,
        maskClosable: false,
        okText: '确定',
        cancelText: '取消',
        onOk: () => {
          dispatch(deleteMemberPoint({ id: record.id ?? record.key, type: 0 }))
            .then(unwrapResult)
            .then(() => {
              notification.success({ message: '删除成功' })
              fetchExchangeProjects()
            })
        },
      })
      return
    }
    if (text === 'edit') {
      setEditExchangeRecord(record)
      editExchangeForm.setFieldsValue({ point: record.point })
      setEditExchangeVisible(true)
    }
  }

  const handleSave = () => {
    form.validateFields().then((values) => {
      setButtonLoading(true)
      const params: MemberPointConfigurationSubmitParams = {
        state: pointsEnabled,
        obtainState: values.obtainState,
        price: values.price != null ? Number(values.price) : undefined,
        point: values.point != null ? Number(values.point) : undefined,
        obtainRange: values.obtainRange,
        resetType: values.resetType,
      }
      if (values.resetType === 1) {
        params.month = fixedClearDate?.[0]
        params.day = fixedClearDate?.[1]
      } else if (values.resetType === 2) {
        params.month = values.month != null ? Number(values.month) : undefined
      }
      dispatch(submitMemberPointConfiguration(params))
        .then(unwrapResult)
        .then(() => {
          notification.success({ message: '保存成功' })
          // 刷新租户配置，确保会员列表积分列随开关（JFHD）显隐
          dispatch(getSettingDetail())
        })
        .finally(() => setButtonLoading(false))
    })
  }

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 'bold',
    borderBottom: '1px dashed #BBBBBB',
    paddingBottom: '10px',
    width: '90%',
    marginBottom: '16px',
  }

  const labelStyle: React.CSSProperties = {
    width: 150,
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: 6,
    lineHeight: '30px',
  }

  return (
    <>
      {!inTab && (
        <NavBar
          style={{ margin: '10px 20px 0px 20px' }}
          where={['会员管理', '会员积分']}
          backtrace={{
            name: '患者管理',
            path: '/statistics/outpatient',
          }}
        />
      )}
      <div className={styles.memberContent} style={inTab ? { margin: '5px 0', height: 'calc(100% - 10px)' } : undefined}>
        <Form
          form={form}
          onFinish={handleSave}
          style={{ height: '100%' }}
          initialValues={{ obtainState: 1, obtainRange: 0 }}
          className={styles.memberPointsForm}
        >
          <div style={{ overflow: 'auto', height: 'calc(100% - 50px)' }}>
            <Row style={sectionTitleStyle}>
              积分
            </Row>

            <Row style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
              <Col style={labelStyle}>积分活动 :</Col>
              <Col style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <Form.Item name="state" style={{ marginBottom: 0 }} valuePropName="checked">
                  <span className={styles.pointsSwitch}>
                    <Switch checkedChildren="开启" unCheckedChildren="关闭" checked={pointsEnabled} onChange={(v) => setPointsEnabled(v)} />
                  </span>
                </Form.Item>
                <span style={{ color: '#999', marginLeft: 10, fontSize: 12 }}>
                  开启后，可设置积分自动获取/兑换/清零规则
                </span>
              </Col>
            </Row>

            {pointsEnabled && (
              <>

            <Row style={sectionTitleStyle}>
              获取规则
            </Row>

            <Row style={{ display: 'flex', marginBottom: '10px' }}>
              <Col style={labelStyle}>获取规则 :</Col>
              <Col style={{ flex: 1 }}>
                <Form.Item name="obtainState" style={{ marginBottom: 0 }} valuePropName="checked" getValueFromEvent={(e) => (e.target.checked ? 1 : 0)}>
                  <Checkbox>成功消费的积分</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Row style={{ display: 'flex', marginBottom: '20px' }}>
              <Col style={labelStyle}></Col>
              <Col style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <span>每消费</span>
                <Form.Item name="price" style={{ marginBottom: 0, marginLeft: 8, marginRight: 8 }} initialValue="1" rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入数字，最多两位小数' }]}>
                  <Input style={{ width: 80 }} />
                </Form.Item>
                <span>元，积</span>
                <Form.Item name="point" style={{ marginBottom: 0, marginLeft: 8, marginRight: 8 }} initialValue="1" rules={[{ pattern: /^\d+$/, message: '请输入整数' }]}>
                  <Input style={{ width: 80 }} />
                </Form.Item>
                <span>分</span>
              </Col>
            </Row>

            <Row style={{ display: 'flex', alignItems: 'flex-start', marginBottom: scope === 1 ? 0 : 20 }}>
              <Col style={labelStyle}>参与范围 :</Col>
              <Col style={{ flex: 1, paddingTop: 4 }}>
                <Form.Item name="obtainRange" style={{ marginBottom: 0 }}>
                  <Radio.Group onChange={(e) => setScope(e.target.value)}>
                    <div style={{ marginBottom: 10 }}>
                      <Radio value={0}>全部项目</Radio>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Radio value={1}>指定项目</Radio>
                      {scope === 1 && (
                        <Space size={10} style={{ marginLeft: 16 }}>
                          <Dropdown
                            trigger={['click']}
                            overlay={
                              <Menu>
                                <Menu.Item key="goodsCategory" onClick={() => setAddItemCategoryVisible(true)}>添加商品分类</Menu.Item>
                                <Menu.Item key="treatmentCategory" onClick={() => setAddTreatmentCategoryVisible(true)}>添加诊疗分类</Menu.Item>
                              </Menu>
                            }
                          >
                            <Button type="link">
                              添加分类 <DownOutlined />
                            </Button>
                          </Dropdown>
                          <Dropdown
                            trigger={['click']}
                            overlay={
                              <Menu>
                                <Menu.Item key="goods" onClick={() => { setAddTarget('scope'); setAddItemVisible(true) }}>添加商品</Menu.Item>
                                <Menu.Item key="treatment" onClick={() => { setAddTarget('scope'); setAddTreatmentVisible(true) }}>添加诊疗</Menu.Item>
                              </Menu>
                            }
                          >
                            <Button type="link">
                              添加项目 <DownOutlined />
                            </Button>
                          </Dropdown>
                        </Space>
                      )}
                    </div>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            {scope === 1 && (
              <div style={{ marginTop: 12, marginBottom: 36, marginLeft: 150, width: 'calc(90% - 150px)' }}>
                <div ref={specifiedGridRef} className={styles.pointsModal}>
                  <SimpleAgGrid
                    rowHeight={41}
                    isLoading={specifiedLoading}
                    column={MemberPointsSpecifiedColumns(handleSpecifiedAction, (specifiedCurrent - 1) * specifiedSize)}
                    page={{
                      items: specifiedProjects,
                      current: specifiedCurrent,
                      size: specifiedSize,
                      total: specifiedTotal,
                    }}
                    onChangePage={(p, s) => {
                      setSpecifiedCurrent(p)
                      if (s) setSpecifiedSize(s)
                    }}
                    getRowId={(r: any) => r.key}
                  />
                </div>
              </div>
            )}

            <Row style={sectionTitleStyle}>
              兑换商品
            </Row>

            <Row style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Col style={labelStyle}>兑换范围 :</Col>
              <Col style={{ flex: 1 }}>
                <Dropdown
                  trigger={['click']}
                  overlay={
                    <Menu>
                      <Menu.Item key="goods" onClick={() => { setAddTarget('exchange'); setAddItemVisible(true) }}>添加商品</Menu.Item>
                      <Menu.Item key="treatment" onClick={() => { setAddTarget('exchange'); setAddTreatmentVisible(true) }}>添加诊疗</Menu.Item>
                    </Menu>
                  }
                >
                  <Button type="link">
                    添加项目 <DownOutlined />
                  </Button>
                </Dropdown>
              </Col>
            </Row>
            <div style={{ marginTop: 12, marginBottom: 36, marginLeft: 150, width: 'calc(90% - 150px)' }}>
              <div ref={exchangeGridRef} className={styles.pointsModal}>
                <SimpleAgGrid
                  rowHeight={41}
                  isLoading={exchangeLoading}
                  column={MemberPointsExchangeColumns(handleExchangeAction, (exchangeCurrent - 1) * exchangeSize)}
                    page={{
                      items: exchangeProjects,
                      current: exchangeCurrent,
                      size: exchangeSize,
                      total: exchangeTotal,
                    }}
                  onChangePage={(p, s) => {
                    setExchangeCurrent(p)
                    if (s) setExchangeSize(s)
                  }}
                  getRowId={(r: any) => r.key}
                />
              </div>
            </div>

            <Row style={sectionTitleStyle}>
              积分有效期
            </Row>

            <Row style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px' }}>
              <Col style={labelStyle}>积分清零类型 :</Col>
              <Col style={{ flex: 1, paddingTop: 4 }}>
                <Form.Item name="resetType" style={{ marginBottom: 0 }} initialValue={0}>
                  <Radio.Group>
                    <div style={{ marginBottom: 10 }}>
                      <Radio value={0}>永久有效</Radio>
                      <span style={{ color: '#999', marginLeft: 10, fontSize: 12 }}>患者获得的积分永久有效</span>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <Radio value={1}>固定日期清零</Radio>
                      <span style={{ color: '#999', marginLeft: 10, fontSize: 12 }}>患者获得的积分在每年固定日期清零</span>
                      <div style={{ marginTop: 8, marginLeft: 24, display: 'flex', alignItems: 'center' }}>
                        <MonthDayPicker value={fixedClearDate} onChange={setFixedClearDate} />
                        <span style={{ color: '#999', marginLeft: 10, fontSize: 12 }}>在日期的当天23:59:59，所有积分将被清零</span>
                      </div>
                    </div>
                    <div>
                      <Radio value={2}>滚动日期清零</Radio>
                      <span style={{ color: '#999', marginLeft: 10, fontSize: 12 }}>患者当月获取的积分会在一定周期后清零</span>
                      <div style={{ marginTop: 8, marginLeft: 24, display: 'flex', alignItems: 'center' }}>
                        <Form.Item name="month" style={{ marginBottom: 0 }} rules={[{ pattern: /^([1-9]|[12]\d|3[0-6])$/, message: '请输入1~36的整数' }]}>
                          <Input style={{ width: 140 }} placeholder='滚动有效期' addonAfter="月" />
                        </Form.Item>
                        <span style={{ color: '#999', marginLeft: 10, fontSize: 12 }}>患者当月获取的积分会在有效期月的最后一天23:59:59 清零</span>
                      </div>
                    </div>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            </>
            )}
          </div>

          <Row style={{ paddingTop: '10px' }}>
            <Col span={12}>
              <Button
                htmlType="button"
                onClick={() => form.resetFields()}
                style={{ float: 'right', marginRight: '10px' }}
              >
                取消
              </Button>
            </Col>
            <Col span={12}>
              <Button
                loading={buttonLoading}
                type="primary"
                htmlType="submit"
                style={{ float: 'left' }}
              >
                保存
              </Button>
            </Col>
          </Row>
        </Form>

        <AddItemForPoints
          visible={addItemVisible}
          type={addTarget === 'exchange' ? 0 : 1}
          onCancel={() => setAddItemVisible(false)}
          onSuccess={() => {
            if (addTarget === 'exchange') {
              fetchExchangeProjects()
            } else {
              fetchSpecifiedProjects()
            }
          }}
        />
        <AddTreatmentForPoints
          visible={addTreatmentVisible}
          type={addTarget === 'exchange' ? 0 : 1}
          onCancel={() => setAddTreatmentVisible(false)}
          onSuccess={() => {
            if (addTarget === 'exchange') {
              fetchExchangeProjects()
            } else {
              fetchSpecifiedProjects()
            }
          }}
        />
        {exceptionCatalogType === 1 ? (
          <AddTreatmentForPoints
            visible={exceptionVisible}
            type={1}
            title={`编辑例外诊疗 - ${exceptionRecord?.name || ''}`}
            defaultSelectedRowKeys={exceptionDefaultKeys}
            defaultSelectedRows={exceptionDefaultRows}
            exceptionBillingCategory={exceptionRecord?.billingCategory}
            onCancel={() => {
              setExceptionVisible(false)
              setExceptionDefaultKeys([])
              setExceptionDefaultRows([])
            }}
            onSuccess={fetchSpecifiedProjects}
          />
        ) : (
          <AddItemForPoints
            visible={exceptionVisible}
            type={1}
            title={`编辑例外商品 - ${exceptionRecord?.name || ''}`}
            defaultSelectedRowKeys={exceptionDefaultKeys}
            defaultSelectedRows={exceptionDefaultRows}
            exceptionBillingCategory={exceptionRecord?.billingCategory}
            onCancel={() => {
              setExceptionVisible(false)
              setExceptionDefaultKeys([])
              setExceptionDefaultRows([])
            }}
            onSuccess={fetchSpecifiedProjects}
          />
        )}
        <AddItemCategoryForPoints
          visible={addItemCategoryVisible}
          isExchange={1}
          onCancel={() => setAddItemCategoryVisible(false)}
          onSuccess={fetchSpecifiedProjects}
        />
        <AddTreatmentCategoryForPoints
          visible={addTreatmentCategoryVisible}
          isExchange={1}
          onCancel={() => setAddTreatmentCategoryVisible(false)}
          onSuccess={fetchSpecifiedProjects}
        />
        <Modal
          title="编辑兑换项目"
          visible={editExchangeVisible}
          maskClosable={false}
          onCancel={() => setEditExchangeVisible(false)}
          onOk={() => {
            editExchangeForm.validateFields().then((values) => {
              const r = editExchangeRecord || {}
              setEditExchangeLoading(true)
              dispatch(submitMemberPoint([{
                id: r.id ?? r.key,
                isExchange: 0,
                name: r.name,
                catalogType: r.catalogType ?? 0,
                tenantCatalogId: r.tenantCatalogId ?? '',
                point: values.point,
              }]))
                .then(unwrapResult)
                .then(() => {
                  notification.success({ message: '保存成功' })
                  setEditExchangeVisible(false)
                  fetchExchangeProjects()
                })
                .finally(() => setEditExchangeLoading(false))
            })
          }}
          confirmLoading={editExchangeLoading}
          destroyOnClose
        >
          <Form form={editExchangeForm} layout="horizontal" labelAlign="right" labelCol={{ span: 5 }} wrapperCol={{ span: 19 }}>
            {editExchangeRecord?.catalogType === 1 ? (
              <>
                <Form.Item label="诊疗名称">
                  <span>{editExchangeRecord?.name}</span>
                </Form.Item>
                <Form.Item label="目录售价">
                  <span>{editExchangeRecord?.retailPrice != null ? Number(editExchangeRecord.retailPrice).toFixed(5) : ''}</span>
                </Form.Item>
                <Form.Item label="成本价">
                  <span>{editExchangeRecord?.costPrice != null && editExchangeRecord.costPrice >= 0 ? Number(editExchangeRecord.costPrice).toFixed(5) : ''}</span>
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item label="商品名称">
                  <span>{editExchangeRecord?.name}</span>
                </Form.Item>
                <Form.Item label="目录售价">
                  <span>{editExchangeRecord?.retailPrice != null ? Number(editExchangeRecord.retailPrice).toFixed(5) : ''}</span>
                </Form.Item>
                <Form.Item label="目录进价">
                  <span>{editExchangeRecord?.stockPrice != null && editExchangeRecord.stockPrice >= 0 ? Number(editExchangeRecord.stockPrice).toFixed(5) : ''}</span>
                </Form.Item>
              </>
            )}
            <Form.Item
              label="积分"
              name="point"
              rules={[
                { required: true, message: '请输入积分' },
                { pattern: /^\d+$/, message: '请输入大于等于0的整数' },
              ]}
            >
              <Input addonAfter="积分" style={{ width: 200 }} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  )
}
